import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import styles from './InspirationsAndPlansView.module.scss'

// For leaving on a screen: an inspiration, slowly changing, beside what you
// said you'd do — today's and tomorrow's plans with their times, and the ones
// for "soon" — and the time. The same screen as the Life app's Apple TV mode.
//
// Plans are private (LifePlan, server schema/plan.js): made in the Life app,
// only ever returned to their owner. Left/right moves the quote, space holds it.

const MY_PLANS_QUERY = gql`
  query MyPlansForScreen {
    myPlans(includeDone: false) {
      identity
      properties { title notes due }
    }
  }
`

// Neo4j's DateTime comes through as an object ({year, month, …, timeZoneOffsetSeconds})
// — there's no serializer for the scalar. Its fields are local to its offset.
const toDate = (dt) => {
  if (!dt) return null
  if (typeof dt === 'string') {
    const d = new Date(dt)
    return isNaN(d.getTime()) ? null : d
  }
  const n = (v) => (v && typeof v === 'object' && 'low' in v ? v.low : v) || 0
  const utc = Date.UTC(n(dt.year), n(dt.month) - 1, n(dt.day) || 1, n(dt.hour), n(dt.minute), n(dt.second))
  return new Date(utc - n(dt.timeZoneOffsetSeconds) * 1000)
}

const sameDay = (a, b) => a.toDateString() === b.toDateString()

// "Tonight 19:00", "Tomorrow 08:00", "Sat 10:00".
const planTime = (date) => {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (sameDay(date, now)) return `${date.getHours() >= 17 ? 'Tonight' : 'Today'} ${time}`
  if (sameDay(date, tomorrow)) return `Tomorrow ${time}`
  return `${date.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`
}

export default function InspirationsAndPlansView({ inspirations }) {
  const quotes = useMemo(() => (inspirations || []).filter((q) => q.source), [inspirations])
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [now, setNow] = useState(new Date())

  // Plans change from the phone; keep up every few minutes.
  const { data } = useQuery(MY_PLANS_QUERY, { pollInterval: 5 * 60 * 1000, fetchPolicy: 'cache-and-network' })

  // A new quote every 40 seconds while playing; the clock every 15.
  useEffect(() => {
    if (!playing || quotes.length < 2) return
    const id = setInterval(() => setIndex((i) => (i + 1) % quotes.length), 40000)
    return () => clearInterval(id)
  }, [playing, quotes.length])
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (!quotes.length) return
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % quotes.length)
      else if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + quotes.length) % quotes.length)
      else if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [quotes.length])

  const plans = (data?.myPlans || []).map((p) => ({ id: p.identity, title: p.properties.title, due: toDate(p.properties.due) }))
  const endOfTomorrow = new Date(now)
  endOfTomorrow.setHours(0, 0, 0, 0)
  endOfTomorrow.setDate(endOfTomorrow.getDate() + 2)
  const upcoming = plans.filter((p) => p.due && p.due < endOfTomorrow).sort((a, b) => a.due - b.due).slice(0, 5)
  const soon = plans.filter((p) => !p.due).slice(0, 4)

  const quote = quotes.length ? quotes[index % quotes.length] : null

  return (
    <div className={styles.view}>
      <div className={styles.quoteSide}>
        {quote ? (
          <div className={styles.quote} key={`${index}-${quote.source}`}>
            <div className={styles.mark}>“</div>
            <div className={styles.text}>{quote.source}</div>
            {quote.sourceLanguage && <div className={styles.lang}>{quote.sourceLanguage}</div>}
          </div>
        ) : (
          <div className={styles.empty}>No inspirations yet — save one from Life.</div>
        )}
      </div>

      <div className={styles.planSide}>
        <div className={styles.clock}>{now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
        <div className={styles.date}>{now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>

        {upcoming.length === 0 && soon.length === 0 && (
          <div className={styles.nothing}>Nothing planned. That's allowed too.</div>
        )}
        {upcoming.length > 0 && (
          <section>
            <div className={styles.label}>You said you'd</div>
            {upcoming.map((p) => (
              <div className={styles.plan} key={p.id}>
                <span className={styles.ring} />
                <div>
                  <div className={styles.title}>{p.title}</div>
                  <div className={styles.time}>{planTime(p.due)}</div>
                </div>
              </div>
            ))}
          </section>
        )}
        {soon.length > 0 && (
          <section>
            <div className={styles.label}>Soon</div>
            {soon.map((p) => (
              <div className={styles.plan} key={p.id}>
                <span className={styles.ring} />
                <div className={styles.title}>{p.title}</div>
              </div>
            ))}
          </section>
        )}
        {!playing && <div className={styles.paused}>Paused — space to carry on</div>}
      </div>
    </div>
  )
}
